// eslint configured: rule not present in CRA build
import { useEffect, useState } from "react";
import { X, Trash2, Wand2 } from "lucide-react";
import { apiMutate, PLATFORMS, STAGES, client } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { markCoachStep } from "@/lib/coach";
import ScriptModal from "@/components/ScriptModal";

const SEAL = "var(--seal)";
const INK_55 = "var(--ink-55)";

export default function IdeaModal({ idea, onClose, onSaved, onDeleted }) {
  const editing = !!idea;
  const [title, setTitle] = useState(idea?.title || "");
  const [description, setDescription] = useState(idea?.description || "");
  // Support both legacy single-platform docs and new multi-platform docs.
  const initialPlatforms = idea?.platforms && idea.platforms.length
    ? idea.platforms
    : [idea?.platform || "youtube_long"];
  const [platforms, setPlatforms] = useState(initialPlatforms);
  const [tags, setTags] = useState(idea?.tags || []);
  const [tagDraft, setTagDraft] = useState("");
  const [stage, setStage] = useState(idea?.stage || "idea");
  const [scheduledFor, setScheduledFor] = useState(idea?.scheduled_for?.slice(0, 10) || "");
  const [saving, setSaving] = useState(false);
  const [showScript, setShowScript] = useState(false);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const togglePlatform = (id) => {
    setPlatforms((cur) => {
      if (cur.includes(id)) {
        // Don't allow zero — keep at least one.
        if (cur.length === 1) return cur;
        return cur.filter((p) => p !== id);
      }
      return [...cur, id];
    });
  };

  const commitTag = () => {
    const t = tagDraft.trim().replace(/,$/, "").trim();
    if (!t) { setTagDraft(""); return; }
    setTags((cur) => cur.includes(t) ? cur : [...cur, t]);
    setTagDraft("");
  };

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTag();
    } else if (e.key === "Backspace" && !tagDraft && tags.length) {
      // Backspace on empty input removes last chip.
      setTags((cur) => cur.slice(0, -1));
    }
  };

  const onTagChange = (e) => {
    const v = e.target.value;
    // Auto-commit when user types a comma (mid-typing or paste).
    if (v.endsWith(",")) {
      setTagDraft(v);
      const t = v.slice(0, -1).trim();
      if (t) {
        setTags((cur) => cur.includes(t) ? cur : [...cur, t]);
        setTagDraft("");
      }
    } else {
      setTagDraft(v);
    }
  };

  const removeTag = (t) => setTags((cur) => cur.filter((x) => x !== t));

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    // Make sure any unsaved tag draft is committed.
    const finalTags = tagDraft.trim()
      ? [...new Set([...tags, tagDraft.trim()])]
      : tags;
    const body = {
      title: title.trim(),
      description,
      platform: platforms[0],
      platforms,
      stage,
      tags: finalTags,
      scheduled_for: scheduledFor ? `${scheduledFor}T00:00:00Z` : null,
    };
    try {
      if (editing) {
        await apiMutate("patch", `/ideas/${idea.id}`, body);
        if (body.stage !== idea?.stage) {
          try { track(EVENTS.IDEA_STAGE_CHANGED, { from: idea?.stage, to: body.stage }); } catch { /* noop */ }
          markCoachStep("move-pipeline");
        }
      } else {
        await apiMutate("post", "/ideas", body);
        try { track(EVENTS.IDEA_CREATED, { platform: body.platform, platforms: body.platforms, has_description: !!body.description }); } catch { /* noop */ }
      }
      onSaved?.();
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this idea?")) return;
    await client.delete(`/ideas/${idea.id}`);
    onDeleted?.();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="idea-modal-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="idea-modal"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10" style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}>
          <h3 className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
            {editing ? "Edit idea" : "New idea"}
          </h3>
          <button onClick={onClose} className="btn-ghost" data-testid="idea-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="eyebrow block mb-2">Title</label>
            <input
              data-testid="idea-title-input"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What&apos;s the spark?"
              className="input text-base"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'wght' 450" }}
            />
          </div>

          <div>
            <label className="eyebrow block mb-2">
              Platform <span className="normal-case tracking-normal" style={{ color: "var(--ink-38)" }}>(pick one or many)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => {
                const selected = platforms.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`idea-platform-${p.id}`}
                    data-selected={selected ? "true" : "false"}
                    onClick={() => togglePlatform(p.id)}
                    className="px-3.5 py-1.5 rounded-full text-xs transition"
                    style={{
                      background: selected ? "var(--ink)" : "transparent",
                      color: selected ? "var(--bg)" : INK_55,
                      border: selected ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {editing && (
            <div>
              <label className="eyebrow block mb-2">Stage</label>
              <select data-testid="idea-stage-select" value={stage} onChange={(e) => setStage(e.target.value)} className="input">
                {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="eyebrow block mb-2">
              Tags <span className="normal-case tracking-normal" style={{ color: "var(--ink-38)" }}>(press comma or Enter)</span>
            </label>
            <div
              className="input flex flex-wrap items-center gap-1.5 min-h-[44px] cursor-text"
              onClick={(e) => {
                const inp = e.currentTarget.querySelector("input");
                inp && inp.focus();
              }}
              data-testid="idea-tags-wrap"
            >
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs"
                  style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.30)", color: "var(--seal)" }}
                  data-testid={`idea-tag-chip-${t}`}
                >
                  {t}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeTag(t); }}
                    className="w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ color: "var(--seal)" }}
                    aria-label={`Remove tag ${t}`}
                    data-testid={`idea-tag-remove-${t}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                data-testid="idea-tags-input"
                value={tagDraft}
                onChange={onTagChange}
                onKeyDown={onTagKey}
                placeholder={tags.length ? "" : "growth, hook, tutorial"}
                className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-0.5"
                style={{ color: "var(--ink)" }}
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">Notes</label>
            <textarea
              data-testid="idea-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="input resize-none"
              placeholder="Rough notes, research, script outline…"
            />
          </div>

          <div>
            <label className="eyebrow block mb-2">Schedule for</label>
            <input data-testid="idea-schedule-input" type="date" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} className="input" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 sticky bottom-0" style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
          {editing ? (
            <div className="flex items-center gap-2">
              <button onClick={del} data-testid="idea-delete-btn" className="btn-ghost" style={{ color: "#c97a6a" }}>
                <Trash2 size={14} /> Delete
              </button>
              <button onClick={() => setShowScript(true)} data-testid="idea-write-script-btn" className="btn-ghost" style={{ color: "var(--seal)" }}>
                <Wand2 size={14} /> Co-Pilot
              </button>
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClose}
              className="btn-secondary"
              data-testid="idea-cancel-btn"
              style={{ touchAction: "manipulation" }}
            >Cancel</button>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={save}
              disabled={saving || !title.trim()}
              className="btn-primary disabled:opacity-50"
              data-testid="idea-save-btn"
              style={{ touchAction: "manipulation" }}
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Save idea"}
            </button>
          </div>
        </div>
      </div>
      {showScript && editing && (
        <ScriptModal idea={idea} onClose={() => setShowScript(false)} onSaved={() => { setShowScript(false); onSaved?.(); }} />
      )}
    </div>
  );
}
